<template>
    <KvPopup class="kv-date-range-picker" @close-outside="handleCancel" ref="dateRangePicker">
        <template #trigger="{ toggle }">
            <slot name="trigger" :toggle="toggle" :range="formattedRange"></slot>
        </template>

        <template #content="{ toggle }">
            <div class="kv-range-dropdown">
                <!-- RANGES PREDEFINIDOS -->
                <div class="kv-predefined">
                    <KvButton v-for="(range, key) in predefinedRanges" :key="key" @click="applyPredefined(range)" class="kv-predefined-btn" variant="text" alignment="left">
                        <!-- i18n-validator-ignore-next-line: displaying dynamic key from predefinedRanges object -->
                        {{ key }}
                    </KvButton>
                </div>

                <!-- CALENDÁRIOS -->
                <div class="kv-calendars">
                    <div v-for="(calendar, idx) in [leftCalendar, rightCalendar]" :key="idx" class="kv-calendar">
                        <div class="kv-calendar-header">
                            <KvButton prepend-icon="ui-chevron-left" @click="changeMonth(idx, -1)" variant="text"></KvButton>
                            <!-- <div>{{ calendar.monthName }} {{ calendar.year }}</div> -->
                            <div class="d-flex align-items-center gap-sm">
                                <KvSelect :style="{ width: '65px' }" variant="tinted" size="small" :value="calendar.month" :options="monthOptions" :clearable="false" value-field="value" label-field="label" :placeholder="t('inputs-datepicker-month')" @input="(val) => handleHeaderChange(idx, 'month', val)" class="kv-month-select" />

                                <KvInput :style="{ width: '80px' }" variant="tinted" size="small" :value="calendar.year" type="number" :placeholder="t('inputs-datepicker-year')" @input="(val) => handleHeaderChange(idx, 'year', val)" class="kv-year-input" />
                            </div>
                            <KvButton prepend-icon="ui-chevron-right" @click="changeMonth(idx, 1)" variant="text"></KvButton>
                        </div>

                        <div class="kv-calendar-weekdays">
                            <span v-for="(d, wi) in daysOfWeek" :key="'wd-' + wi">{{ d }}</span>
                        </div>

                        <div class="kv-calendar-days">
                            <span v-for="day in calendar.days" :key="day.toISODate()" :class="{
                                'kv-day': true,
                                'kv-outside': day.month !== calendar.month,
                                'kv-selected': isSelected(day),
                                'kv-in-range': isInRange(day),
                                'kv-today': isToday(day),
                                'kv-disabled': isDayDisabled(day)
                            }" @click="!isDayDisabled(day) && selectDay(day)">
                                {{ day.day }}
                            </span>
                        </div>
                    </div>

                </div>
                <div class="kv-actions-wrapper">
                    <KvButton @click="handleCancel(toggle())" variant="outlined">{{ t('inputs-daterangepicker-cancel') }}</KvButton>
                    <KvButton @click="applyRange(toggle())" prepend-icon="ui-check">{{ t('inputs-daterangepicker-apply') }}</KvButton>
                </div>
            </div>
        </template>

    </KvPopup>
</template>

<script>
import { DateTime, Info } from "luxon";
import KvPopup from "@/components/ui/kv-popup.vue";
import KvButton from "@/components/form/kv-button.vue";
import KvInput from "@/components/inputs/kv-input.vue";
import KvSelect from "@/components/inputs/kv-select.vue";

export default {
    name: "KvDateRangePicker",
    components: { KvPopup, KvButton, KvInput, KvSelect },
    props: {
        value: {
            required: false,
        },
        allPeriod: {
            type: Object,
        },
        maxRangeMonths: {
            type: Number,
            default: null
        }
    },
    data() {
        const today = DateTime.now();
        return {
            start: today.minus({ days: 30 }),
            end: today,
            today,
            leftMonth: today.startOf("month"),
            rightMonth: today.plus({ months: 1 }).startOf("month"),
        };
    },
    mounted() {
        if (this.value && this.value.start && this.value.end) {
            this.start = this.toDateTime(this.value.start);
            this.end = this.toDateTime(this.value.end);
        }
    },
    computed: {
        daysOfWeek() {
            // Same order as kv-date-picker and calendar grid (Sunday-first columns).
            return [
                this.t('inputs-datepicker-weekdays-sun'),
                this.t('inputs-datepicker-weekdays-mon'),
                this.t('inputs-datepicker-weekdays-tue'),
                this.t('inputs-datepicker-weekdays-wed'),
                this.t('inputs-datepicker-weekdays-thu'),
                this.t('inputs-datepicker-weekdays-fri'),
                this.t('inputs-datepicker-weekdays-sat'),
            ];
        },
        monthOptions() {
            return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((index) => {
                const label = this.t(`dates-full_months-${index}`);
                return {
                    label: (label.charAt(0).toUpperCase() + label.slice(1)).slice(0, 3),
                    value: index + 1 // Luxon usa 1-12
                };
            });
        },
        formattedRange() {
            return `${this.start.toFormat("dd/MM/yyyy")} - ${this.end.toFormat("dd/MM/yyyy")}`;
        },
        predefinedRanges() {
            const t = this.today.startOf("day");
            const base = {};
            base[this.t('inputs-daterangepicker-today')] = [t, t];
            base[this.t('inputs-daterangepicker-yesterday')] = [t.minus({ days: 1 }), t.minus({ days: 1 })];
            base[this.t('inputs-daterangepicker-this_month')] = [t.startOf("month"), t.endOf("month")];
            base[this.t('inputs-daterangepicker-this_year')] = [t.startOf("year"), t.endOf("year")];
            base[this.t('inputs-daterangepicker-last_month')] = [
                t.minus({ months: 1 }).startOf("month"),
                t.minus({ months: 1 }).endOf("month"),
            ];
            base[this.t('inputs-daterangepicker-last_year')] = [
                t.minus({ years: 1 }).startOf("year"),
                t.minus({ years: 1 }).endOf("year"),
            ];

            if (this.allPeriod) {
                base[this.t('inputs-daterangepicker-all_period')] = [
                    this.toDateTime(this.allPeriod.start),
                    this.toDateTime(this.allPeriod.end),
                ];
            }
            return base;
        },
        leftCalendar() {
            return this.buildCalendar(this.leftMonth);
        },
        rightCalendar() {
            return this.buildCalendar(this.rightMonth);
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        toggleShow() {
            this.$refs.dateRangePicker.toggleShow();
        },
        alignCalendars() {
            this.leftMonth = this.start.startOf("month");
            this.rightMonth = this.end.startOf("month");
        },
        toDateTime(value) {
            if (!value) return null;

            if (value instanceof DateTime) {
                return value;
            }

            if (value instanceof Date) {
                return DateTime.fromJSDate(value);
            }

            if (typeof value === "string") {
                // ISO válido
                let dt = DateTime.fromISO(value);
                if (dt.isValid) return dt;

                // Formato SQL: "YYYY-MM-DD HH:mm:ss"
                dt = DateTime.fromFormat(value, "yyyy-MM-dd HH:mm:ss");
                if (dt.isValid) return dt;
            }

            console.warn("Data inválida recebida:", value);
            return null;
        },
        handleHeaderChange(index, type, value) {
            const val = parseInt(value);
            if (!val) return;

            if (type === "year" && val.toString().length != 4) return;

            // Define qual data base estamos alterando (esquerda ou direita)
            let baseDate = index === 0 ? this.leftMonth : this.rightMonth;

            // Cria a nova data com o valor alterado
            let newDate = baseDate.set({ [type]: val });

            if (index === 0) {
                // Alterou ESQUERDA
                this.leftMonth = newDate;

                this.start = this.start.set({ [type]: val });

                // Validação: Esquerda não pode ser maior ou igual à Direita
                if (this.leftMonth >= this.rightMonth) {
                    this.rightMonth = this.leftMonth.plus({ months: 1 });
                }

                if (this.start > this.end) {
                    this.end = this.start;
                }
            } else {
                // Alterou DIREITA
                this.rightMonth = newDate;

                this.end = this.end.set({ [type]: val });

                // Validação: Direita não pode ser menor ou igual à Esquerda
                if (this.rightMonth <= this.leftMonth) {
                    this.leftMonth = this.rightMonth.minus({ months: 1 });
                }

                if (this.end < this.start) {
                    this.start = this.end;
                }
            }
        },
        handleCancel() {
            if (this.value && this.value.start && this.value.end) {
                this.start = this.toDateTime(this.value.start);
                this.end = this.toDateTime(this.value.end);
            } else {
                this.start = this.today.minus({ days: 30 });
                this.end = this.today;
            }
        },
        buildCalendar(base) {
            const startOfMonth = base.startOf("month");
            const endOfMonth = base.endOf("month");

            const startOffset = startOfMonth.weekday % 7;
            const endOffset = 6 - (endOfMonth.weekday % 7);

            const start = startOfMonth.minus({ days: startOffset });
            const end = endOfMonth.plus({ days: endOffset });
            const days = [];
            let current = start;
            while (current <= end) {
                days.push(current);
                current = current.plus({ days: 1 });
            }
            return {
                month: base.month,
                year: base.year,
                monthName: base.setLocale(this.$root.locale).toFormat("LLLL"),
                days,
            };
        },
        changeMonth(idx, dir) {
            if (idx === 0) {
                // Move o calendário da ESQUERDA
                this.leftMonth = this.leftMonth.plus({ months: dir });

                // Garante que o da direita esteja sempre depois
                if (
                    this.leftMonth.year > this.rightMonth.year ||
                    (this.leftMonth.year === this.rightMonth.year &&
                        this.leftMonth.month >= this.rightMonth.month)
                ) {
                    this.rightMonth = this.leftMonth.plus({ months: 1 });
                }
            } else {
                // Move o calendário da DIREITA
                this.rightMonth = this.rightMonth.plus({ months: dir });

                // Garante que o da esquerda esteja sempre antes
                if (
                    this.rightMonth.year < this.leftMonth.year ||
                    (this.rightMonth.year === this.leftMonth.year &&
                        this.rightMonth.month <= this.leftMonth.month)
                ) {
                    this.leftMonth = this.rightMonth.minus({ months: 1 });
                }
            }
        },
        selectDay(day) {
            // Controla para marcar uma nova seleção
            if ((day < this.start || (day > this.start && day <= this.end)) || (this.start != this.end && day > this.end)) {
                this.end = day;
                this.start = day;
            }
            // Controla a incrementar a seleção
            else if (day >= this.start) {
                if (this.maxRangeMonths) {
                    const diff = day.diff(this.start, 'months').months;
                    if (diff > this.maxRangeMonths) {
                        this.end = this.start.plus({ months: this.maxRangeMonths }).endOf('day');
                        return;
                    }
                }
                this.end = day;
            }
        },
        isSelected(day) {
            return day.hasSame(this.start, "day") || day.hasSame(this.end, "day");
        },
        isInRange(day) {
            return day > this.start && day < this.end;
        },
        isToday(day) {
            return day.hasSame(this.today, "day");
        },
        isDayDisabled(day) {
            if (!this.maxRangeMonths) return false;
            if (this.start.hasSame(this.end, 'day')) {
                const diff = day.diff(this.start, 'months').months;
                return diff > this.maxRangeMonths || diff < 0;
            }
            return false;
        },
        applyPredefined([start, end]) {
            if (this.maxRangeMonths) {
                const diff = end.diff(start, 'months').months;
                if (diff > this.maxRangeMonths) {
                    end = start.plus({ months: this.maxRangeMonths }).endOf('day');
                }
            }
            this.start = start;
            this.end = end;
            this.alignCalendars();
        },
        applyRange() {
            this.emitChange();
        },
        emitChange() {
            this.$emit("input", {
                start: this.start.startOf("day").toJSDate(),
                end: this.end.endOf("day").toJSDate(),
            });
        },
    },
    watch: {
        value: {
            immediate: true,
            deep: true,
            handler(val) {
                if (val && val.start && val.end) {
                    this.start = this.toDateTime(val.start);
                    this.end = this.toDateTime(val.end);

                    this.alignCalendars();
                }
            }
        }
    }
};
</script>

<style lang="less">
.kv-date-range-picker {
    display: inline-flex;
    flex-direction: column;
    position: relative;

    .kv-range-wrapper {
        cursor: pointer;
        position: relative;
    }

    .kv-range-display {
        color: #2AAA4C;
        font-size: 13px;
    }

    .kv-range-dropdown {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 16px;

        @media (max-width: 768px) {
            display: flex;
            flex-direction: column;
            max-width: calc(100vw - 75px);

            .kv-predefined {
                width: 100%;
                flex-direction: row;
                overflow: auto;

                .kv-predefined-btn {
                    flex: 0 0 auto;
                    white-space: nowrap;
                }
            }

            .kv-calendars {
                flex-direction: column;
            }

            .kv-actions-wrapper {
                align-self: end;
            }
        }
    }

    .kv-predefined {
        display: flex;
        flex-direction: column;
        width: max-content;
        padding: 8px;
        border-radius: 8px;
    }

    .kv-calendars {
        display: flex;
        gap: 10px;
        padding: 8px;
        border-radius: 8px;
    }

    .kv-calendar {
        padding: 8px;
        border-radius: 8px;
        display: inline-block;

        .kv-calendar-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 6px;
        }

        .kv-calendar-weekdays {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            text-align: center;
            font-size: 11px;
            color: #1f2a37;
            font-weight: 600;
        }

        .kv-calendar-days {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            margin-top: 4px;

            .kv-day {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 38px;
                height: 28px;
                font-size: 12px;
                position: relative;
                cursor: pointer;
                user-select: none;
                color: #1f2a37;

                &:hover {
                    background: #F1FCF3;
                }

                &.kv-outside {
                    color: #ccc;
                }

                &.kv-in-range {
                    background-color: #F1FCF3;
                    color: #2AAA4C;
                }

                &.kv-today::after {
                    content: "";
                    position: absolute;
                    bottom: 2px;
                    left: 50%;
                    width: 4px;
                    height: 4px;
                    margin-left: -2px;
                    border-radius: 50%;
                    background-color: #2AAA4C;
                }

                &.kv-selected.kv-today::after {
                    background-color: #FFF;
                }

                &.kv-selected {
                    background-color: #2AAA4C;
                    color: #FFF;
                    border-radius: 4px;
                }

                &.kv-disabled {
                    opacity: 0.2;
                    cursor: not-allowed;
                    pointer-events: none;
                }
            }
        }
    }

    .kv-actions-wrapper {
        grid-column: 2;
        display: flex;
        align-items: center;
        justify-self: end;
        gap: 6px
    }
}
</style>
